import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { DOCUMENT, DecimalPipe } from '@angular/common';
import { OverlayModule, type ConnectedPosition, CdkConnectedOverlay } from '@angular/cdk/overlay';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagIcon } from '../icon/icon';
import { ScrollIntoViewDirective } from '../scroll-into-view/scroll-into-view.directive';
import { FormField } from '../form-field/form-field';

type TimeFormat = '24h' | '12h';
type Period = 'AM' | 'PM';

/** Minimum number of items in a scroll column for infinite-scroll looping to be useful. */
const MIN_LOOP_ITEMS = 8;

/**
 * Apply a time mask to a raw input string.
 * Returns the masked value (digits + colons only, limited by the max digit count).
 */
function applyTimeMask(raw: string, maxDigits: number): string {
  const digits = raw.replaceAll(/\D/g, '').slice(0, maxDigits);
  let result = '';
  for (let i = 0; i < digits.length; i++) {
    if (i === 2 || i === 4) result += ':';
    result += digits[i];
  }
  return result;
}

/**
 * Return the time mask placeholder string for a given format / seconds config.
 */
function timeMaskPlaceholder(fmt: TimeFormat, showSecs: boolean): string {
  const base = showSecs ? 'HH:MM:SS' : 'HH:MM';
  return fmt === '12h' ? `${base} AM` : base;
}

/** Convert 12-hour time to 24-hour format. Returns null if hours are invalid for 12h format. */
function convertTo24Hour(hours: number, isPM: boolean): number | null {
  if (hours < 1 || hours > 12) return null;
  if (isPM && hours !== 12) return hours + 12;
  if (!isPM && hours === 12) return 0;
  return hours;
}

/** Check if time components are within valid ranges. */
function isValidTimeRange(hours: number, minutes: number, seconds: number): boolean {
  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59 && seconds >= 0 && seconds <= 59;
}

/**
 * Parse a decimal-hours string (e.g. "8.5") into { hours, minutes, seconds }.
 * Returns null when the value is not a finite non-negative number or exceeds 23h.
 */
function parseDecimalHours(raw: string): { hours: number; minutes: number; seconds: number } | null {
  const val = Number(raw);
  if (!Number.isFinite(val) || val < 0) return null;
  const totalMinutes = Math.round(val * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 23) return null;
  return { hours, minutes, seconds: 0 };
}

@Component({
  selector: 'zag-time-picker',
  imports: [OverlayModule, ZagIcon, DecimalPipe, TranslatePipe, ScrollIntoViewDirective],
  templateUrl: './time-picker.html',
  styleUrl: './time-picker.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePicker),
      multi: true,
    },
  ],
  host: {
    class: 'zag-time-picker',
    '(document:keydown.escape)': 'onEscape()',
  },
})
export class TimePicker implements ControlValueAccessor, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly hostRef = inject(ElementRef<HTMLElement>);
  private readonly document = inject(DOCUMENT);
  private readonly formField = inject(FormField, { optional: true });
  protected readonly fieldLabel = computed(() => this.formField?.label() ?? '');

  @ViewChild(CdkConnectedOverlay) private connectedOverlay?: CdkConnectedOverlay;

  private rafId?: number;
  private lastOverlayWidth?: number;
  private readonly mobileQuery =
    typeof this.document.defaultView?.matchMedia === 'function' ? this.document.defaultView.matchMedia('(max-width: 539px)') : null;
  protected readonly isMobile = signal<boolean>(this.mobileQuery?.matches ?? false);
  private readonly onMobileChange = (e: MediaQueryListEvent) => {
    this.isMobile.set(e.matches);
    this.cdr.markForCheck();
  };

  readonly placeholder = input<string>('Select time');
  /**
   * Default time to pre-select in the scroll picker when no value is set.
   * Accepts a time string in HH:MM or HH:MM:SS format.
   * When not provided, defaults to the current time (useful for time-of-day pickers).
   * Set to '00:00' for duration pickers (e.g. break time) where current time makes no sense.
   */
  readonly defaultValue = input<string | undefined>(undefined);
  readonly disabled = input<boolean>(false);
  readonly readonly = input<boolean>(false);
  readonly id = input<string | undefined>(undefined);
  readonly name = input<string | undefined>(undefined);
  readonly required = input<boolean>(false);
  readonly clearable = input<boolean>(true);
  readonly format = input<TimeFormat>('24h');
  readonly showSeconds = input<boolean>(false);
  readonly minuteStep = input<number>(15);
  readonly hourStep = input<number>(1);
  readonly min = input<string | undefined>(undefined);
  readonly max = input<string | undefined>(undefined);
  /** When true, the text input shows a time format mask (e.g. HH:MM or HH:MM:SS) */
  readonly maskInput = input<boolean>(false);
  /**
   * When true, accepts decimal hour notation in the text input (e.g. "8.5" → 08:30).
   * The stored and emitted value is always HH:MM format.
   */
  readonly allowDecimal = input<boolean>(false);
  /**
   * When true, the input's right corners are flattened so it can sit flush
   * against a trailing addon whose container owns the outer border radius.
   */
  readonly flushRight = input<boolean>(false);
  readonly value = model<string>('');

  protected readonly maskPlaceholder = computed(() => timeMaskPlaceholder(this.format(), this.showSeconds()));

  protected readonly open = signal<boolean>(false);
  protected readonly hours = signal<number>(0);
  protected readonly minutes = signal<number>(0);
  protected readonly seconds = signal<number>(0);
  protected readonly period = signal<Period>('AM');
  protected readonly inputValue = signal<string>('');
  protected readonly inputError = signal<boolean>(false);

  /** Hours filtered by hourStep for scroll list */
  protected readonly allHours = computed(() => {
    const step = Math.max(1, this.hourStep());
    return Array.from({ length: Math.ceil(24 / step) }, (_, i) => i * step);
  });
  /** Minutes filtered by minuteStep for scroll list */
  protected readonly allMinutes = computed(() => {
    const step = Math.max(1, this.minuteStep());
    return Array.from({ length: Math.ceil(60 / step) }, (_, i) => i * step);
  });
  /** All seconds 0–59 for scroll list */
  protected readonly allSeconds: number[] = Array.from({ length: 60 }, (_, i) => i);
  /** Period options */
  protected readonly periods: Period[] = ['AM', 'PM'];

  /**
   * Looped arrays — each column renders 3 copies of the real items so the
   * user can scroll continuously. The middle copy is always the "real" one.
   * When there are too few items looping is disabled (single copy).
   */
  protected readonly loopedHours = computed(() => {
    const h = this.allHours();
    return h.length >= MIN_LOOP_ITEMS ? [...h, ...h, ...h] : h;
  });
  protected readonly isHoursLooped = computed(() => this.allHours().length >= MIN_LOOP_ITEMS);
  protected readonly loopedMinutes = computed(() => {
    const m = this.allMinutes();
    return m.length >= MIN_LOOP_ITEMS ? [...m, ...m, ...m] : m;
  });
  protected readonly isMinutesLooped = computed(() => this.allMinutes().length >= MIN_LOOP_ITEMS);
  protected readonly loopedSeconds: number[] = [...this.allSeconds, ...this.allSeconds, ...this.allSeconds];

  protected readonly overlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 4,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -4,
    },
  ];

  /** On mobile the overlay is centred in the viewport via the panel class. */
  protected readonly mobileOverlayPositions: ConnectedPosition[] = [
    { originX: 'center', originY: 'center', overlayX: 'center', overlayY: 'center' },
  ];

  protected readonly displayValue = computed(() => {
    const value = this.value();
    if (!value) return this.placeholder();
    const parsed = this.parseTime(value);
    if (!parsed) return this.placeholder();
    return this.formatTimeForDisplay(parsed.hours, parsed.minutes, parsed.seconds);
  });

  protected readonly showClearButton = computed(() => this.clearable() && this.value() !== '' && !this.disabled() && !this.readonly());

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: string) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  constructor() {
    this.mobileQuery?.addEventListener('change', this.onMobileChange);

    effect(() => {
      if (this.disabled()) {
        this.open.set(false);
        this.stopPositionMonitoring();
        this.cdr.markForCheck();
      }
    });

    effect(() => {
      if (this.open() && !this.isMobile()) {
        this.startPositionMonitoring();
      } else {
        this.stopPositionMonitoring();
      }
    });
  }

  ngOnDestroy(): void {
    this.stopPositionMonitoring();
    this.mobileQuery?.removeEventListener('change', this.onMobileChange);
  }

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
    const parsed = this.parseTime(value ?? '');
    if (parsed) {
      this.hours.set(parsed.hours);
      this.minutes.set(parsed.minutes);
      this.seconds.set(parsed.seconds);
      if (this.format() === '12h') {
        this.period.set(parsed.hours >= 12 ? 'PM' : 'AM');
      }
      this.inputValue.set(this.formatTimeForDisplay(parsed.hours, parsed.minutes, parsed.seconds));
    } else {
      this.inputValue.set('');
    }
    this.inputError.set(false);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean): void {
    this.cdr.markForCheck();
  }

  private openDropdown(): void {
    if (this.disabled() || this.readonly() || this.open()) return;
    const parsed = this.parseTime(this.value());
    if (parsed) {
      this.applyTime(parsed);
    } else {
      this.applyTime(this.resolveDefaultTime());
    }
    this.open.set(true);
    setTimeout(() => this.scrollColumnsToSelection(), 0);
  }

  /** Resolve the initial time to show when the picker has no value. */
  private resolveDefaultTime(): { hours: number; minutes: number; seconds: number } {
    const defaultParsed = this.parseTime(this.defaultValue() ?? '');
    if (defaultParsed) return defaultParsed;
    const now = new Date();
    return { hours: now.getHours(), minutes: now.getMinutes(), seconds: now.getSeconds() };
  }

  /** Apply a parsed time to the hour/minute/second/period signals. */
  private applyTime(time: { hours: number; minutes: number; seconds: number }): void {
    this.hours.set(time.hours);
    this.minutes.set(time.minutes);
    this.seconds.set(time.seconds);
    if (this.format() === '12h') {
      this.period.set(time.hours >= 12 ? 'PM' : 'AM');
    }
  }

  /**
   * Scroll each scroll column so the currently selected item in the **middle
   * copy** is centred. Must be called after the overlay has rendered.
   */
  private scrollColumnsToSelection(): void {
    const overlayEl = this.connectedOverlay?.overlayRef?.overlayElement;
    if (!overlayEl) return;
    for (const column of Array.from(overlayEl.querySelectorAll<HTMLElement>('.scroll-column'))) {
      this.jumpToMiddleCopy(column);
    }
  }

  /**
   * Scroll the given column so the selected item is centred.
   * For looped columns (marked with `scroll-column--looped`) this targets
   * the middle copy; for non-looped columns it scrolls the single list.
   */
  private jumpToMiddleCopy(column: HTMLElement): void {
    const items = Array.from(column.querySelectorAll<HTMLElement>('.scroll-column__item'));
    if (items.length === 0) return;

    const isLooped = column.classList.contains('scroll-column--looped');
    if (!isLooped) {
      const selected = items.find(el => el.getAttribute('aria-selected') === 'true');
      (selected ?? items[0])?.scrollIntoView({ block: 'center', behavior: 'instant' });
      return;
    }

    const count = items.length / 3; // real item count in one copy
    // Find the selected item in the middle copy (indices count..2*count-1)
    for (let i = count; i < 2 * count; i++) {
      if (items[i]?.getAttribute('aria-selected') === 'true') {
        items[i].scrollIntoView({ block: 'center', behavior: 'instant' });
        return;
      }
    }
    // Fallback: scroll to the first item of the middle copy
    items[count]?.scrollIntoView({ block: 'center', behavior: 'instant' });
  }

  /**
   * Called on every scroll event of a looped scroll column.
   * If the scroll position drifts into the first or last copy, silently
   * teleport back to the same visual position in the middle copy.
   * Non-looped columns (no `scroll-column--looped` class) are ignored.
   */
  protected onColumnScroll(event: Event): void {
    const column = event.target as HTMLElement;
    if (!column.classList.contains('scroll-column--looped')) return;
    const items = Array.from(column.querySelectorAll<HTMLElement>('.scroll-column__item'));
    if (items.length === 0) return;
    const count = items.length / 3;
    const itemHeight = column.scrollHeight / items.length;
    const copyHeight = itemHeight * count;
    const scrollTop = column.scrollTop;
    // If scrolled into the first copy (before middle), jump forward by one copy-height
    if (scrollTop < copyHeight) {
      column.scrollTop = scrollTop + copyHeight;
    }
    // If scrolled into the last copy (past middle), jump backward by one copy-height
    else if (scrollTop >= copyHeight * 2) {
      column.scrollTop = scrollTop - copyHeight;
    }
  }

  protected close(): void {
    this.open.set(false);
    this.onTouched();
  }

  protected onEscape(): void {
    if (this.open()) this.close();
  }

  protected clear(event: Event): void {
    event.stopPropagation();
    this.value.set('');
    this.onChange('');
    this.inputValue.set('');
    this.inputError.set(false);
    this.open.set(false);
    this.onTouched();
  }

  /** Select a specific hour from the scroll list */
  protected selectHour(h: number): void {
    if (this.disabled() || this.readonly()) return;
    if (this.format() === '12h') {
      const currentPM = this.period() === 'PM';
      let display24 = h;
      if (currentPM && h < 12) display24 = h + 12;
      else if (!currentPM && h >= 12) display24 = h - 12;
      this.hours.set(display24);
    } else {
      this.hours.set(h);
    }
    this.minutes.set(0);
    setTimeout(() => this.scrollColumnsToSelection(), 0);
  }

  /** Select a specific minute from the scroll list */
  protected selectMinute(m: number): void {
    if (this.disabled() || this.readonly()) return;
    this.minutes.set(m);
  }

  /** Select a specific second from the scroll list */
  protected selectSecond(s: number): void {
    if (this.disabled() || this.readonly()) return;
    this.seconds.set(s);
  }

  /** Select AM or PM from the scroll list */
  protected selectPeriod(p: Period): void {
    if (this.disabled() || this.readonly()) return;
    if (p === this.period()) return;
    this.period.set(p);
    const h = this.hours();
    if (p === 'PM' && h < 12) this.hours.set(h + 12);
    else if (p === 'AM' && h >= 12) this.hours.set(h - 12);
  }

  /** Get display label for a raw 0–23 hour value */
  protected displayHourFor(h: number): string {
    if (this.format() === '12h') {
      return String(h % 12 || 12).padStart(2, '0');
    }
    return String(h).padStart(2, '0');
  }

  protected isHourSelected(h: number): boolean {
    return this.hours() === h;
  }

  protected isMinuteSelected(m: number): boolean {
    return this.minutes() === m;
  }

  protected isSecondSelected(s: number): boolean {
    return this.seconds() === s;
  }

  protected isPeriodSelected(p: Period): boolean {
    return this.period() === p;
  }

  protected setNow(): void {
    if (this.disabled() || this.readonly()) return;
    const now = new Date();
    this.hours.set(now.getHours());
    this.minutes.set(now.getMinutes());
    this.seconds.set(now.getSeconds());
    if (this.format() === '12h') {
      this.period.set(now.getHours() >= 12 ? 'PM' : 'AM');
    }
    this.commitTime();
    setTimeout(() => this.scrollColumnsToSelection(), 0);
  }

  protected apply(): void {
    if (!this.isTimeValid(this.hours(), this.minutes(), this.seconds())) {
      this.inputError.set(true);
      return;
    }
    this.commitTime();
    this.close();
  }

  private commitTime(): void {
    const timeString = this.formatTime(this.hours(), this.minutes(), this.seconds());
    this.value.set(timeString);
    this.onChange(timeString);
    this.inputValue.set(this.formatTimeForDisplay(this.hours(), this.minutes(), this.seconds()));
    this.inputError.set(false);
  }

  private parseTime(value: string | null | undefined): { hours: number; minutes: number; seconds: number } | null {
    if (!value) return null;
    const parts = value.split(':');
    if (parts.length < 2) return null;
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    const seconds = parts.length > 2 ? Number(parts[2]) : 0;
    if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds)) return null;
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) return null;
    return { hours, minutes, seconds };
  }

  private formatTime(hours: number, minutes: number, seconds: number): string {
    const h = String(hours).padStart(2, '0');
    const m = String(minutes).padStart(2, '0');
    if (this.showSeconds()) {
      const s = String(seconds).padStart(2, '0');
      return `${h}:${m}:${s}`;
    }
    return `${h}:${m}`;
  }

  private formatTimeForDisplay(hours: number, minutes: number, seconds: number): string {
    if (this.format() === '12h') {
      const displayHours = hours % 12 || 12;
      const p = hours >= 12 ? 'PM' : 'AM';
      const h = String(displayHours).padStart(2, '0');
      const m = String(minutes).padStart(2, '0');
      if (this.showSeconds()) {
        const s = String(seconds).padStart(2, '0');
        return `${h}:${m}:${s} ${p}`;
      }
      return `${h}:${m} ${p}`;
    }
    return this.formatTime(hours, minutes, seconds);
  }

  protected isTimeValid(hours: number, minutes: number, seconds: number): boolean {
    const timeValue = hours * 3600 + minutes * 60 + seconds;
    return this.isAboveMin(timeValue) && this.isBelowMax(timeValue);
  }

  private isAboveMin(timeValue: number): boolean {
    const minTime = this.parseTime(this.min());
    if (!minTime) return true;
    return timeValue >= minTime.hours * 3600 + minTime.minutes * 60 + minTime.seconds;
  }

  private isBelowMax(timeValue: number): boolean {
    const maxTime = this.parseTime(this.max());
    if (!maxTime) return true;
    return timeValue <= maxTime.hours * 3600 + maxTime.minutes * 60 + maxTime.seconds;
  }

  protected onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (this.maskInput()) {
      const maxDigits = this.showSeconds() ? 6 : 4;
      const masked = applyTimeMask(target.value, maxDigits);
      this.inputValue.set(masked);
      target.value = masked;
    } else {
      this.inputValue.set(target.value);
    }
    // Live-update the scroll columns when the typed text is already a valid time
    const parsed = this.parseUserInput(this.inputValue().trim());
    if (parsed && this.isTimeValid(parsed.hours, parsed.minutes, parsed.seconds)) {
      this.hours.set(parsed.hours);
      this.minutes.set(parsed.minutes);
      this.seconds.set(parsed.seconds);
      if (this.format() === '12h') {
        this.period.set(parsed.hours >= 12 ? 'PM' : 'AM');
      }
      if (this.open()) {
        setTimeout(() => this.scrollColumnsToSelection(), 0);
      }
    }
  }

  protected onInputBlur(): void {
    this.onTouched();
    this.validateAndApplyInput();
  }

  /**
   * Validate the current input value and apply it if valid.
   * Returns true when the time was successfully committed, false otherwise.
   */
  private validateAndApplyInput(): boolean {
    const input = this.inputValue().trim();

    if (!input) {
      this.handleEmptyInput();
      return !this.required();
    }

    const parsed = this.parseUserInput(input);
    if (!parsed || !this.isTimeValid(parsed.hours, parsed.minutes, parsed.seconds)) {
      this.inputError.set(true);
      return false;
    }

    this.applyParsedTime(parsed);
    return true;
  }

  protected onInputKeyDown(event: KeyboardEvent): void {
    if (this.maskInput()) {
      this.handleMaskedTimeKeyDown(event);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.onTouched();
      const applied = this.validateAndApplyInput();
      if (applied && this.open()) {
        this.close();
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      if (this.open()) this.close();
    } else if (event.key === 'ArrowDown' || event.key === 'F4') {
      event.preventDefault();
      if (!this.open()) this.openDropdown();
    }
  }

  /** Handle keydown events in mask mode. */
  private handleMaskedTimeKeyDown(event: KeyboardEvent): void {
    const allowed = [
      'Backspace',
      'Delete',
      'Enter',
      'Escape',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'ArrowDown',
      'ArrowUp',
      'F4',
      'Home',
      'End',
    ];
    if (allowed.includes(event.key)) {
      this.handleMaskedSpecialKey(event);
      return;
    }
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  /** Handle special keys (Enter, Escape, ArrowDown, F4) in mask mode. */
  private handleMaskedSpecialKey(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.onTouched();
      const applied = this.validateAndApplyInput();
      if (applied && this.open()) {
        this.close();
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      if (this.open()) this.close();
    } else if (event.key === 'ArrowDown' || event.key === 'F4') {
      event.preventDefault();
      if (!this.open()) this.openDropdown();
    }
  }

  private handleEmptyInput(): void {
    if (this.required()) {
      this.inputError.set(true);
    } else {
      this.value.set('');
      this.onChange('');
      this.inputError.set(false);
    }
  }

  private applyParsedTime(parsed: { hours: number; minutes: number; seconds: number }): void {
    const { hours, minutes, seconds } = parsed;
    this.hours.set(hours);
    this.minutes.set(minutes);
    this.seconds.set(seconds);
    if (this.format() === '12h') {
      this.period.set(hours >= 12 ? 'PM' : 'AM');
    }
    this.inputError.set(false);
    this.commitTime();
    this.inputValue.set(this.formatTimeForDisplay(hours, minutes, seconds));
  }

  protected onInputFocus(): void {
    this.openDropdown();
    if (this.isMobile()) return;
    const inputElement = this.document.activeElement as HTMLInputElement;
    if (inputElement) {
      setTimeout(() => inputElement.select(), 0);
    }
  }

  protected parseUserInput(input: string): { hours: number; minutes: number; seconds: number } | null {
    const trimmed = input.trim().toLowerCase();
    if (this.allowDecimal() && /^\d+(\.\d+)?$/.test(trimmed)) {
      return parseDecimalHours(trimmed);
    }
    const { isPM, hasAmPm, cleanInput } = this.extractAmPm(trimmed);
    const parsed = this.parseTimeComponents(cleanInput.trim());
    if (!parsed) return null;
    const hours = hasAmPm ? convertTo24Hour(parsed.hours, isPM) : parsed.hours;
    if (hours === null) return null;
    const { minutes, seconds } = parsed;
    return isValidTimeRange(hours, minutes, seconds) ? { hours, minutes, seconds } : null;
  }

  private extractAmPm(trimmed: string): { isPM: boolean; hasAmPm: boolean; cleanInput: string } {
    if (trimmed.includes('pm') || trimmed.endsWith('p')) {
      return { isPM: true, hasAmPm: true, cleanInput: trimmed.replaceAll(/[ \t]*(?:pm|p)[ \t]*/gi, '') };
    }
    if (trimmed.includes('am') || trimmed.endsWith('a')) {
      return { isPM: false, hasAmPm: true, cleanInput: trimmed.replaceAll(/[ \t]*(?:am|a)[ \t]*/gi, '') };
    }
    return { isPM: false, hasAmPm: false, cleanInput: trimmed };
  }

  private parseTimeComponents(cleanInput: string): { hours: number; minutes: number; seconds: number } | null {
    if (cleanInput.includes(':')) return this.parseColonFormat(cleanInput);
    return this.parseDigitFormat(cleanInput);
  }

  private parseColonFormat(input: string): { hours: number; minutes: number; seconds: number } | null {
    const parts = input.split(':');
    if (parts.length < 2 || parts.length > 3) return null;
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    const seconds = parts.length > 2 ? Number(parts[2]) : 0;
    if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds)) return null;
    return { hours, minutes, seconds };
  }

  private parseDigitFormat(input: string): { hours: number; minutes: number; seconds: number } | null {
    const digits = input.replaceAll(/\D/g, '');

    switch (digits.length) {
      case 1:
      case 2:
        return { hours: Number(digits), minutes: 0, seconds: 0 };
      case 3:
        return { hours: Number(digits[0]), minutes: Number(digits.slice(1)), seconds: 0 };
      case 4:
        return { hours: Number(digits.slice(0, 2)), minutes: Number(digits.slice(2)), seconds: 0 };
      case 5:
        return { hours: Number(digits[0]), minutes: Number(digits.slice(1, 3)), seconds: Number(digits.slice(3)) };
      case 6:
        return { hours: Number(digits.slice(0, 2)), minutes: Number(digits.slice(2, 4)), seconds: Number(digits.slice(4)) };
      default:
        return null;
    }
  }

  private startPositionMonitoring(): void {
    const check = () => {
      if (!this.open()) return;
      const overlayRef = this.connectedOverlay?.overlayRef;
      const container = this.hostRef.nativeElement.querySelector('.time-picker-container') as HTMLElement | null;
      if (overlayRef && container) {
        const w = Math.round(container.getBoundingClientRect().width);
        if (this.lastOverlayWidth !== w) {
          overlayRef.updateSize({ minWidth: w });
          this.lastOverlayWidth = w;
        }
        overlayRef.updatePosition();
      }
      this.rafId = requestAnimationFrame(check);
    };
    this.rafId = requestAnimationFrame(check);
  }

  private stopPositionMonitoring(): void {
    if (this.rafId !== undefined) {
      cancelAnimationFrame(this.rafId);
      this.rafId = undefined;
    }
    this.lastOverlayWidth = undefined;
  }
}
